import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const FilterControls = ({ filters, onFilterChange, onClearFilters, resultCount }) => {
  const transactionStageOptions = [
    { value: 'all', label: 'All Stages' },
    { value: 'initiated', label: 'Initiated' },
    { value: 'documentation', label: 'Documentation' },
    { value: 'review', label: 'Under Review' },
    { value: 'signatures', label: 'Collecting Signatures' },
    { value: 'completion', label: 'Near Completion' },
    { value: 'completed', label: 'Completed' }
  ];

  const propertyTypeOptions = [
    { value: 'all', label: 'All Property Types' },
    { value: 'residential', label: 'Residential' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'industrial', label: 'Industrial' },
    { value: 'land', label: 'Land/Plot' },
    { value: 'mixed-use', label: 'Mixed Use' }
  ];

  const stakeholderRoleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'owner', label: 'Property Owner' },
    { value: 'lawyer', label: 'Legal Counsel' },
    { value: 'engineer', label: 'Engineer' },
    { value: 'notary', label: 'Notary' },
    { value: 'broker', label: 'Broker' }
  ];

  const deadlineProximityOptions = [
    { value: 'all', label: 'All Deadlines' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'today', label: 'Due Today' },
    { value: 'week', label: 'Due This Week' },
    { value: 'month', label: 'Due This Month' },
    { value: 'future', label: 'Future Deadlines' }
  ];

  const trlRangeOptions = [
    { value: 'all', label: 'All TRL Scores' },
    { value: '80-100', label: '80-100% (Ready)' },
    { value: '60-79', label: '60-79% (In Progress)' },
    { value: '40-59', label: '40-59% (Developing)' },
    { value: '0-39', label: '0-39% (Early Stage)' }
  ];

  const sortOptions = [
    { value: 'trl-desc', label: 'TRL Score (High to Low)' },
    { value: 'trl-asc', label: 'TRL Score (Low to High)' },
    { value: 'deadline-asc', label: 'Deadline (Nearest First)' },
    { value: 'deadline-desc', label: 'Deadline (Farthest First)' },
    { value: 'value-desc', label: 'Property Value (High to Low)' },
    { value: 'value-asc', label: 'Property Value (Low to High)' },
    { value: 'updated-desc', label: 'Recently Updated' },
    { value: 'created-desc', label: 'Recently Created' }
  ];

  const handleFilterChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const hasActiveFilters = Object.values(filters)?.some(value => 
    value && value !== 'all' && value !== ''
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground flex items-center">
          <Icon name="Filter" size={20} className="mr-2" />
          Filter & Sort Transactions
        </h2>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-muted-foreground">
            {resultCount} transaction{resultCount !== 1 ? 's' : ''} found
          </span>
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClearFilters}
              iconName="X"
              iconPosition="left"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>
      {/* Search Bar */}
      <div className="mb-4">
        <Input
          type="search"
          placeholder="Search by property title, location, transaction ID..."
          value={filters?.search || ''}
          onChange={(e) => handleFilterChange('search', e?.target?.value)}
          className="w-full"
        />
      </div>
      {/* Filter Controls Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-4">
        <Select
          label="Transaction Stage"
          options={transactionStageOptions}
          value={filters?.stage || 'all'}
          onChange={(value) => handleFilterChange('stage', value)}
        />

        <Select
          label="Property Type"
          options={propertyTypeOptions}
          value={filters?.propertyType || 'all'}
          onChange={(value) => handleFilterChange('propertyType', value)}
        />

        <Select
          label="My Role"
          options={stakeholderRoleOptions}
          value={filters?.stakeholderRole || 'all'}
          onChange={(value) => handleFilterChange('stakeholderRole', value)}
        />

        <Select
          label="Deadline Proximity"
          options={deadlineProximityOptions}
          value={filters?.deadlineProximity || 'all'}
          onChange={(value) => handleFilterChange('deadlineProximity', value)}
        />

        <Select
          label="TRL Score Range"
          options={trlRangeOptions}
          value={filters?.trlRange || 'all'}
          onChange={(value) => handleFilterChange('trlRange', value)}
        />
      </div>
      {/* Sort and View Options */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0 sm:space-x-4">
        <div className="flex items-center space-x-4">
          <Select
            label="Sort By"
            options={sortOptions}
            value={filters?.sortBy || 'trl-desc'}
            onChange={(value) => handleFilterChange('sortBy', value)}
            className="w-48"
          />
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">View:</span>
          <Button
            variant={filters?.viewMode === 'grid' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => handleFilterChange('viewMode', 'grid')}
            iconName="Grid3X3"
          />
          <Button
            variant={filters?.viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => handleFilterChange('viewMode', 'list')}
            iconName="List"
          />
        </div>
      </div>
      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Active filters:</span>
            {Object.entries(filters)?.map(([key, value]) => {
              if (!value || value === 'all' || value === '' || key === 'viewMode' || key === 'sortBy') return null;
              
              let displayValue = value;
              if (key === 'search') displayValue = `"${value}"`;
              
              return (
                <span
                  key={key}
                  className="inline-flex items-center px-2 py-1 text-xs bg-primary/10 text-primary rounded-full"
                >
                  {displayValue}
                  <button
                    onClick={() => handleFilterChange(key, key === 'search' ? '' : 'all')}
                    className="ml-1 hover:text-primary/80"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterControls;